import React from "react";
import {
  Typography,
  Grid,
  Modal,
  Box,
  Tabs,
  Tab,
  IconButton,
  TextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { Close, RefreshRounded } from "@mui/icons-material";
import LocateLand from "./LocateLand";
import GenericButton from "./Custom/GenericButton";
import { useFarmMapModal } from "../hooks/useFarmMapModal";

const modalStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100dvw",
  height: "calc(100dvh - 64px)",
  transform: "none",
  bgcolor: "background.paper",
  display: "flex",
  flexDirection: "column",
  border: "none",
  overflow: "hidden",
  margin: 0,
  padding: 0,
};

const headerStyle = {
  width: "100%",
  height: "64px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#4CAF50",
  padding: "5px 10px",
  color: "white",
};

const mapContainerStyle = {
  width: "100%",
  display: "flex",
  flexGrow: 1,
  position: "relative",
  marginTop: 1,
};

const controlsStyle = {
  width: "100%",
  height: "auto",
};

const FarmMapModal = ({
  open,
  center,
  handleClose,
  path,
  setPath,
  setUserCenter,
  location,
  setCurrentLocation,
  recenter,
  errors,
  t,
  isScriptLoaded,
  isEdit,
}) => {
  const {
    activeTab,
    manualLat,
    manualLng,
    forceRecenter,
    useCustomCoordinates,
    isPolygonSaved,
    setManualLat,
    setManualLng,
    setUseCustomCoordinates,
    handleTabChange,
    handleSave,
    handleModalClose,
    addVertexToWalkingTab,
    applyManualCoordinates,
    resetCurrentTab,
    getValidCenter,
  } = useFarmMapModal({
    open,
    center,
    location,
    path,
    setPath,
    setUserCenter,
    setCurrentLocation,
    isEdit,
  });

  const renderControls = () => (
    <Grid
      container
      spacing={2}
      alignItems="center"
      justifyContent="center"
      padding={2}
    >
      {activeTab === 1 ? (
        <>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomCoordinates}
                  onChange={(e) => setUseCustomCoordinates(e.target.checked)}
                  color="primary"
                />
              }
              label={t("Use Custom Coordinates")}
            />
          </Grid>

          {useCustomCoordinates && (
            <Grid item xs={12}>
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={3.5}>
                  <TextField
                    fullWidth
                    label={t("Latitude")}
                    value={manualLat}
                    onChange={(e) => setManualLat(e.target.value)}
                    variant="outlined"
                    size="small"
                    type="number"
                    inputProps={{ step: "0.000001" }}
                  />
                </Grid>
                <Grid item xs={3.5}>
                  <TextField
                    fullWidth
                    label={t("Longitude")}
                    value={manualLng}
                    onChange={(e) => setManualLng(e.target.value)}
                    variant="outlined"
                    size="small"
                    type="number"
                    inputProps={{ step: "0.000001" }}
                  />
                </Grid>
                <Grid item xs={5}>
                  <GenericButton
                    onClick={applyManualCoordinates}
                    backgroundColor="#4CAF50"
                    fontColor="white"
                    padding="8px 16px"
                    fontSize="14px"
                    fullWidth
                  >
                    {t("Apply")}
                  </GenericButton>
                </Grid>
              </Grid>
            </Grid>
          )}

          <Grid item xs={6}>
            <GenericButton
              onClick={resetCurrentTab}
              backgroundColor="#f44336"
              fontColor="white"
              icon={RefreshRounded}
              padding="8px 16px"
              fontSize="14px"
              fullWidth
            >
              {t("Reset")}
            </GenericButton>
          </Grid>
          <Grid item xs={6}>
            <GenericButton
              onClick={() => handleSave(handleClose)}
              backgroundColor="#4CAF50"
              fontColor="white"
              disabled={path.length < 3}
              padding="8px 16px"
              fontSize="14px"
              fullWidth
            >
              {t("Save")}
            </GenericButton>
          </Grid>
        </>
      ) : (
        <>
          <Grid item xs={4}>
            <GenericButton
              onClick={addVertexToWalkingTab}
              backgroundColor="#4CAF50"
              fontColor="white"
              padding="8px 16px"
              fontSize="14px"
              fullWidth
            >
              {t("AddVertex")}
            </GenericButton>
          </Grid>
          <Grid item xs={4}>
            <GenericButton
              onClick={() => recenter()}
              backgroundColor="#4CAF50"
              fontColor="white"
              padding="8px 16px"
              fontSize="14px"
              fullWidth
            >
              {t("Recenter")}
            </GenericButton>
          </Grid>
          <Grid item xs={4}>
            <GenericButton
              onClick={() => handleSave(handleClose)}
              backgroundColor="#4CAF50"
              fontColor="white"
              disabled={path.length < 3}
              padding="8px 16px"
              fontSize="14px"
              fullWidth
            >
              {t("Save")}
            </GenericButton>
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Grid container justifyContent="center">
              <Grid item xs={4}>
                <GenericButton
                  onClick={resetCurrentTab}
                  backgroundColor="#f44336"
                  fontColor="white"
                  icon={RefreshRounded}
                  padding="8px 16px"
                  fontSize="14px"
                  fullWidth
                >
                  {t("Reset")}
                </GenericButton>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );

  return (
    <Modal
      open={open}
      onClose={() => handleModalClose(handleClose)}
      aria-labelledby="farm-map-modal"
      aria-describedby="farm-boundary-mapping"
    >
      <Box sx={modalStyle}>
        <Box sx={headerStyle}>
          <Typography
            variant="h6"
            component="h2"
            sx={{ fontWeight: 500, fontSize: "1.25rem" }}
          >
            {t("Map your farm")}
          </Typography>
          <IconButton
            onClick={() => handleModalClose(handleClose)}
            aria-label="close"
            size="large"
            sx={{ color: "white", marginRight: "10px" }}
          >
            <Close />
          </IconButton>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "white" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ minHeight: "48px" }}
          >
            <Tab
              label={
                isPolygonSaved
                  ? `${t("Map by walking")} (${t("Disabled")})`
                  : t("Map by walking")
              }
              disabled={isPolygonSaved}
              sx={{
                textTransform: "none",
                minHeight: "48px",
                opacity: isPolygonSaved ? 0.5 : 1,
              }}
            />
            <Tab
              label={t("Mark boundary")}
              sx={{ textTransform: "none", minHeight: "48px" }}
            />
          </Tabs>
        </Box>

        <Box sx={mapContainerStyle}>
          <LocateLand
            center={getValidCenter()}
            path={path}
            setPath={setPath}
            setCenter={setUserCenter}
            editable={true}
            currentLocation={location}
            setCurrentLocation={setCurrentLocation}
            style={{ width: "100%", height: "100%" }}
            createInitialRectangle={activeTab === 1}
            mapHeight={"100%"}
            isScriptLoaded={isScriptLoaded}
            forceRecenter={forceRecenter}
            preventAutoCenter={activeTab === 1} // Prevent auto-centering in boundary tab
          />
        </Box>

        <Box sx={controlsStyle}>
          {renderControls()}
          {errors.farm_location && (
            <Typography color="error" variant="caption">
              {errors.farm_location.latitude || errors.farm_location.longitude}
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default FarmMapModal;
